import { useCallback, useContext, useRef } from "react";

import { Models } from "api/types";
import { useAppState } from "contexts/AppContext";
import { queryClient, ThemeContext } from "pages/Root";
import Services from "services/Services";
import { currencyService } from "toolbox/CurrencyService";
import { changeDayjsLocale } from "utils/dayjsUtils";

/**
 * Applies the practice a patient is acting as: swaps the API host, auth token, branding, and
 * currency, then clears cached data belonging to the previous practice.
 *
 * Shared by the practice picker and by arrival push notifications, which can target a practice the
 * patient is not currently on — the check-in screen would otherwise load against the wrong host.
 *
 * `switchToPlatform` keeps one identity for the life of the component. The push notification
 * context tears down and re-registers the native listeners whenever its deep-link handler changes,
 * and both `useAppState()` and the theme context hand out a new object on every render, so reading
 * them through a ref is what stops an arrival tap from landing in a listener gap.
 *
 * Resolves `true` when it swapped practices (and cleared the cache), `false` when the patient was
 * already acting as that practice.
 */
export const useSwitchPlatform = () => {
  const appState = useAppState();
  const { resetTheme } = useContext(ThemeContext);
  const latest = useRef({ appState, resetTheme });
  latest.current = { appState, resetTheme };

  const switchToPlatform = useCallback(
    async (
      platform: Models<"GetMultiPlatformUserPlatformResult">,
      allPlatforms: Models<"GetMultiPlatformUserPlatformResult">[],
    ): Promise<boolean> => {
      const { appState: state, resetTheme: reset } = latest.current;
      const domainScheme = new URL(import.meta.env.REACT_APP_API_BASE_URL ?? location.href)
        .protocol;
      const apiUrl = `${domainScheme}//${platform.platformDomain}`;

      // Already acting as this practice: nothing to swap.
      if (state.apiUrl === apiUrl) {
        return false;
      }

      state.setApiUrl(apiUrl);
      state.setAppIcons(allPlatforms.map((entry) => `launcher_${entry.platformSubdomain}`));
      state.setPlatformId(platform.platformId);
      await Services.auth.setToken(platform.token);

      if (platform.platformStripeRegion) {
        changeDayjsLocale(platform.platformStripeRegion);
        currencyService.setCurrency(platform.platformStripeRegion);
      }

      reset();
      queryClient.clear();
      return true;
    },
    [],
  );

  return { switchToPlatform };
};
